/* eslint-disable import/no-default-export */
import type { JwtConfig } from './jwtDefaultConfig'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import type { PluginObject } from 'vue'

import jwtDefaultConfig from './jwtDefaultConfig'
import axios from 'axios'
import moment from 'moment'
import store from '@/store'

export type JwtRefreshListener = (token: string) => void
export type ExpiredListener = (status: string) => void

type AvailableLocales = 'es' | 'en'

export default class JwtService {
    // Will be used by this service for making API calls
    axiosIns: Nullable<AxiosInstance> = null

    // jwtConfig <= Will be used by this service
    jwtConfig: JwtConfig = { ...jwtDefaultConfig }

    // For Refreshing Token
    isAlreadyFetchingAccessToken = false

    // For Refreshing Token
    subscribers: JwtRefreshListener[] = []

    expiredListeners: ExpiredListener[] = []

    #expiredListener?: ExpiredListener

    #refreshSem: Promise<void> | null = null

    constructor(axiosIns: AxiosInstance, jwtOverrideConfig: JwtConfig) {
        this.axiosIns = axiosIns
        this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

        // Request Interceptor
        this.axiosIns.interceptors.request.use(
            config => {
                // Get token from localStorage
                const accessToken = this.getToken()
                const language = this.getLanguage()

                // If token is present add it to request's Authorization Header
                if (accessToken) {
                    config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
                }
                if (language) {
                    config.headers['X-localization'] = String(language)
                }
                return config
            },
            error => Promise.reject(error)
        )

        // Add request/response interceptor
        this.axiosIns.interceptors.response.use(
            response => {
                // Refresh token if has activity less than JWT_REFRESH_TTL - JWT_TTL minutes
                if (import.meta.env.VITE_JWT_TTL && import.meta.env.VITE_JWT_REFRESH_TTL && this.getExpires()) {
                    const expiresAt = moment(this.getExpires())
                    const refreshThreshold = moment(expiresAt).subtract(
                        import.meta.env.VITE_JWT_REFRESH_TTL - import.meta.env.VITE_JWT_TTL,
                        'minutes'
                    )
                    if (moment().isBetween(refreshThreshold, expiresAt)) {
                        return this.retryWithNewToken(response.config)
                    }
                }
                return response
            },
            error => {
                // Only we intercept when the error is caused by axios
                if (!axios.isAxiosError(error)) return Promise.reject(error)
                // const { config, response: { status } } = error
                const { config, response } = error
                const originalRequest = config

                // if (status === 401) {
                if (response?.status === 401 && originalRequest) {
                    return this.retryWithNewToken(originalRequest)
                }

                return Promise.reject({
                    ...response?.data,
                    _status_code: response?.status,
                    _response: response,
                })
            }
        )
    }

    async retryWithNewToken(request: InternalAxiosRequestConfig) {
        type RefreshResponse = {
            accessToken: string
            refreshToken: string
            expires_at: string
        }
        try {
            if (!this.isAlreadyFetchingAccessToken) {
                this.isAlreadyFetchingAccessToken = true
                this.#refreshSem = (async () => {
                    try {
                        const { data } = await axios.post<RefreshResponse>(
                            this.jwtConfig.refreshEndpoint,
                            {
                                refreshToken: this.getRefreshToken(),
                            },
                            {
                                headers: {
                                    Authorization: `${this.jwtConfig.tokenType} ${this.getToken()}`,
                                },
                            }
                        )
                        this.setToken(data.refreshToken)
                        this.setExpires(data.expires_at)
                        this.onAccessTokenFetched(data.accessToken)
                    } finally {
                        // Automatically collect the modified values
                        this.#refreshSem = null
                        this.isAlreadyFetchingAccessToken = false
                    }
                })()
            }
            // Now we should have a token
            await this.#refreshSem
            if (!this.axiosIns) throw new Error('Invalid state, axios was not installed')
            return this.axiosIns(request)
        } catch (error) {
            // If we are here, then the token couldn't be refreshed
            let status = 'expired'
            if (axios.isAxiosError(error)) {
                const { response } = error
                status = response?.data?.status ?? 'expired'
            }
            this.removeToken(status)
            throw error
        }
    }

    removeToken(status: string) {
        this._cleanAuthData()
        this.expiredListeners.forEach(listener => listener(status))
        this.#expiredListener?.(status)
    }

    _cleanAuthData() {
        // TODO: ALl The cleaning
        localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
        localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
        localStorage.removeItem(this.jwtConfig.tokenExpireAt)
        // Remove userData from localStorage
        localStorage.removeItem('userData')
        localStorage.removeItem('empresa')
        // Remove the web push token
        if (this.getWebPushToken()) {
            const webPushToken = this.getWebPushToken()
            store.dispatch('auth/deleteWebPushToken', { token: webPushToken })
            localStorage.removeItem(this.jwtConfig.storageWebPushTokenName)
        }
        // console.info('Auth Cleaned')
    }

    setExpiredListener(expiredListener: ExpiredListener) {
        this.#expiredListener = expiredListener
        // console.dir(this.#expiredListener)
    }

    addOnExpireListener(listener: ExpiredListener) {
        // console.info('installing expire listener')
        Object.defineProperty(listener, '__listener_hash', {
            configurable: false,
            value: Date.now() * performance.now(),
        })
        this.expiredListeners.push(listener)
        // console.dir(this.expiredListeners)
    }

    onAccessTokenFetched(accessToken: string) {
        this.subscribers = this.subscribers.filter(callback => callback(accessToken))
    }

    addSubscriber(callback: JwtRefreshListener) {
        this.subscribers.push(callback)
    }

    getToken() {
        return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
    }

    getExpires() {
        return localStorage.getItem(this.jwtConfig.tokenExpireAt)
    }

    getLanguage() {
        return localStorage.getItem(this.jwtConfig.storageLanguageKeyName) ?? 'es'
    }

    getRefreshToken() {
        return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
    }

    getWebPushToken() {
        return localStorage.getItem(this.jwtConfig.storageWebPushTokenName)
    }

    setToken(value: string) {
        localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
    }

    setExpires(value: string) {
        localStorage.setItem(this.jwtConfig.tokenExpireAt, value)
    }

    setLanguage(value: AvailableLocales) {
        localStorage.setItem(this.jwtConfig.storageLanguageKeyName, value)
    }

    setRefreshToken(value: string) {
        localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
    }

    setWebPushToken(token: string) {
        localStorage.setItem(this.jwtConfig.storageWebPushTokenName, token)
    }

    login(...args: Nullable<Record<string, unknown>>[]) {
        return this.axiosIns?.post(this.jwtConfig.loginEndpoint, ...args)
    }

    register(...args: Record<string, unknown>[]) {
        return this.axiosIns?.post(this.jwtConfig.registerEndpoint ?? '', ...args)
    }

    refreshToken() {
        // if (!this.axiosIns) throw Error('No Axios Instance Attached')
        return axios.post(this.jwtConfig.refreshEndpoint, {
            refreshToken: this.getRefreshToken(),
        })
    }

    logout() {
        return this.axiosIns?.post(this.jwtConfig.logoutEndpoint)
    }
}

let ServiceInstance: JwtService | null = null

export const useJwt = () => ServiceInstance

export const JwtPlugin: PluginObject<{ axiosInstance: AxiosInstance; router: any }> = {
    install(vue, options) {
        // console.log('Installing JWT Auth Plugin', vue)
        if (!options?.axiosInstance) {
            throw new Error('No axios instance was given to the JWT Service')
        }
        if (!options?.router) {
            throw new Error('No router instance was provided')
        }
        ServiceInstance = new JwtService(options?.axiosInstance, jwtDefaultConfig)
        ServiceInstance.setExpiredListener(status => {
            // console.log('Trying to redirect onto login')
            options.router.replace({ name: 'auth-login', params: { alert: status } })
        })
    },
}
